// src/components/game/panels/PlayerStatusPanel/PlayerStatusPanel.jsx
import React, { useMemo } from "react";
import useAuthStore from "@store/useAuthStore";
import useGameStore from "@store/useGameStore";
import { PlayerStatusInGame } from "@utils/enums.js";
import { Zap, Users, Map } from 'lucide-react';
import "./PlayerStatusPanel.css";
import Tabs from "@components/ui/Tabs/Tabs";
import TurnTag from "@components/ui/general/turn-tag";
import GameProgressBar from "@components/ui/game/game-progress-bar";
import GameStatusButtonPanel from "@components/game/panels/GameStatusButtonPanel/GameStatusButtonPanel";
import Minimap from "@components/game/panels/Minimap/Minimap";
import StatBar from "@components/ui/general/StatBar";
import { EMPTY_ARRAY } from "@utils/constants";
import { useT } from '@/i18n';

export default function PlayerStatusPanel() {
  const t = useT();

  /* ---------- stores ---------- */
  const { user } = useAuthStore();
  const game     = useGameStore((s) => s.game);          // <-- suscrito
  // Removed isPlanPhase - no longer needed here
  

  /* ---------- derived ---------- */
  const players = useMemo(() => game?.players || EMPTY_ARRAY, [game]);
  const current = players.find((p) => p.user_id === user?.id) ?? null;
  // Removed isPlayerReady - no longer needed here
  
//console.log("[PlayerStatusPanel] Current player:", current?.name, "Status:", current);

 // const turnNumber = game?.turn_number ?? 0; // Corregido para usar turn_number
  
  /* turn, status, etc. */
  const gameName   = game?.name  ?? "Game";
  const turnNumber = game?.turn_number;
  // Removed isLoading state - no longer needed here


  const sortedPlayers = useMemo(
    () =>
      [...players].sort(
        (a, b) => (a.pivot?.player_order ?? 0) - (b.pivot?.player_order ?? 0)
      ),
    [players]
  );

  /* ---------- helpers ---------- */
  const getStatusInfo = (status) => {
    switch (status) {
      case PlayerStatusInGame.DEPLOYING:  return { textKey:"player_status.deploying",  cls:"status-deploying",  ch:"D" };
      case PlayerStatusInGame.DEPLOYED:   return { textKey:"player_status.deployed",   cls:"status-deployed",   ch:"✔" };
      case PlayerStatusInGame.PLANNING:   return { textKey:"player_status.planning",   cls:"status-planning",   ch:"P" };
      case PlayerStatusInGame.PLANNED:    return { textKey:"player_status.resolving",  cls:"status-resolving",  ch:"R" };
      default:                            return { textKey:"player_status.unknown",    cls:"status-unknown",    ch:"?" };
    }
  };

  // Removed handleFinalizePlanning - now handled by GameStatusButtonPanel

  const renderPlayer = (p) => {
    //console.log("Rendering player:"+ p.name, "Status:", p);
    const { textKey, cls, ch } = getStatusInfo(p.pivot?.status_in_game);
    const statusText = t(textKey);
    
    // ✅ VALIDACIÓN MEJORADA - Evitar valores NaN en el cálculo de porcentaje
    let pct = 0;
    
    if (p.pivot?.max_energy && p.pivot?.energy !== undefined) {
      const energy = Number(p.pivot.energy);
      const maxEnergy = Number(p.pivot.max_energy);
      
      // Verificar que ambos valores sean números válidos
      if (!isNaN(energy) && !isNaN(maxEnergy) && 
          isFinite(energy) && isFinite(maxEnergy) && 
          maxEnergy > 0) {
        pct = (energy / maxEnergy) * 100;
        
        // Asegurar que el porcentaje esté en rango válido
        pct = Math.max(0, Math.min(100, pct));
      } else {
        console.warn("[PlayerStatusPanel] Valores de energía inválidos para jugador:", p.name, {
          energy: p.pivot.energy,
          maxEnergy: p.pivot.max_energy
        });
      }
    }

    // ✅ VALIDACIÓN FINAL - Verificar que pct sea un número válido
    if (isNaN(pct) || !isFinite(pct)) {
      console.error("[PlayerStatusPanel] Porcentaje calculado es inválido:", pct);
      pct = 0; // Fallback seguro
    }

    return (
      <div key={p.id} className="player-status-item" title={statusText}>
        <div className="player-row">
          <div className={`status-circle ${cls}`} title={statusText}>{ch}</div>
          <span className={`player-name${p.id === current?.id ? " current-player" : ""}`}>
            {p.name}
            {p.type === 'ai' && ' 🤖'}
            <span className="player-id-hidden">[{p.id}]</span>
          </span>
        </div>
        <StatBar
          value={p.pivot?.energy || 0}
          maxValue={p.pivot?.max_energy || 1}
          colorMode="single"
          borderColor="var(--color-cyan)"
          textStrokeColor="#000"
          icon={<Zap className="energy-icon" size={20} fill="var(--color-text-dark)" />}
          className="energy-bar"
        />
      </div>
    );
  };

  /* ---------- render ---------- */
  // Status tab content
  const statusContent = (
    <div className="player-status-content">
      {!current ? (
        <p className="empty-msg error">
          {t('player_status.no_current_player')}
        </p>
      ) : (
        <>
          {/* Header compacto: título + progress bar con turn */}
          <div className="game-header">
            <span className="game-title">{gameName}</span>
            <div className="game-progress-row">
              <GameProgressBar status={game.status} />
              <TurnTag turnNumber={turnNumber} status={game.status} />
            </div>
          </div>

          {/* lista de jugadores */}
          {sortedPlayers.map(renderPlayer)}

          {/* botón de acción del juego */}
          <GameStatusButtonPanel />
        </>
      )}
    </div>
  );

  // Tab configuration
  const tabs = [
    {
      id: 'status',
      label: t('player_status.tab_players'),
      icon: <Users size={14} />,
      content: statusContent
    },
    {
      id: 'minimap',
      label: t('player_status.tab_map'),
      icon: <Map size={14} />,
      content: <Minimap />
    }
  ];

  return (
    <div className="player-status-wrapper panel">
      <Tabs tabs={tabs} defaultTab="status" />
    </div>
  );
}

